import { z } from 'zod';
import { BankStatementLineSchema, LineSuggestionSchema, RehydratedAllocationLegSchema, ReconcilableInvoiceSchema, ReconcilableSupplierInvoiceSchema, } from './bank-statement.js';
import { LedgerEntrySetSchema } from '../ledger.js';
import { CalendarDateOnlySchema, ClassificationOutcomeSchema, LowercaseUuidSchema } from './common.js';
import { nullableText } from '../sanitised-text.js';
// =============================================================================
// Shared leg schemas (must be declared before any schema that references them)
// =============================================================================
/**
 * Leg kind enum for proposal legs.
 * Mirrors the Prisma ProposalLegKind enum on the wire.
 *
 * Every kind except `CREATE_EXPENSE` is a posting instruction the approve
 * endpoint replays through the reconcile engines. `CREATE_EXPENSE` is the one
 * NON-POSTABLE kind: a flag saying this payee's history shows the firm normally
 * raises an expense (supplier invoice) and settles it, so the line should become
 * one rather than being posted straight to a ledger account. It carries no
 * foreign key and no amount, it is always a proposal's only leg, and approving a
 * proposal containing one is rejected server-side before any engine dispatch -
 * the UI offers Dismiss only.
 *
 * `TRANSFER` is an inter-account transfer leg: the line is one side of a
 * movement between two of the firm's own bank accounts. It carries
 * `bankAccountId` (the counterpart bank account) and the SIGNED line amount,
 * must be a proposal's only leg, and approval dispatches it to the
 * bank-transfer engine (posting this side via Transfer Clearing) rather than
 * to the reconcile engines.
 */
export const ProposalLegKindSchema = z.enum([
    'PAYMENT',
    'INVOICE',
    'EXPENSE',
    'CREDIT_NOTE',
    'SPLIT',
    'CLIENT_ALLOCATION',
    'CREATE_EXPENSE',
    'TRANSFER',
]);
/**
 * Source enum for proposals.
 */
export const ProposalSourceSchema = z.enum(['SYSTEM', 'MANUAL']);
/**
 * What a proposal leg names, resolved server-side so every reader (web, CLI,
 * MCP, chat) gets the same facts without a second lookup. `kind` is the record
 * the leg points at, which is not always the leg's own kind: a PAYMENT leg
 * takes the subject of the document its payment settles, or of the party a
 * document-free payment carries. `MISSING` means the referenced record is gone
 * (its label reads "(deleted)").
 *
 * INVOICE: `outstandingMinorUnits` is what the invoice owes now;
 * `outstandingAfterMinorUnits` is what it would owe once this leg posts (now
 * minus the leg amount for an INVOICE leg, unchanged for a PAYMENT leg, whose
 * payment is already recorded). Both are pinned by the proposal fingerprint, so
 * an approve that pins it posts exactly this: an `outstandingAfterMinorUnits`
 * of 0 or less means the invoice is paid in full once the leg posts.
 */
export const LegSubjectSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('LEDGER_ACCOUNT'),
        name: z.string(),
        code: z.string(),
        isVatControl: z.boolean(),
        archived: z.boolean(),
    }),
    z.object({
        kind: z.literal('INVOICE'),
        invoiceNumber: z.string().nullable(),
        clientName: z.string(),
        outstandingMinorUnits: z.number().int(),
        outstandingAfterMinorUnits: z.number().int(),
        currency: z.string(),
    }),
    z.object({
        kind: z.literal('EXPENSE'),
        supplierName: z.string(),
        supplierInvoiceNumber: z.string().nullable(),
    }),
    z.object({
        kind: z.literal('CREDIT_NOTE'),
        creditNoteNumber: z.string().nullable(),
        clientName: z.string(),
    }),
    z.object({ kind: z.literal('CLIENT'), clientName: z.string() }),
    z.object({ kind: z.literal('SUPPLIER'), supplierName: z.string() }),
    z.object({ kind: z.literal('BANK_ACCOUNT'), bankAccountName: z.string() }),
    z.object({ kind: z.literal('MISSING') }),
]);
/**
 * Length of `proposalFingerprint`: hex characters of a hash over what a
 * proposal would post (merged legs' kind, references and amounts) and the
 * outstanding of every invoice it settles, so a payment elsewhere against one of
 * those invoices changes it. Confidence, the classification decision,
 * timestamps, leg ids and the note are left out, so a re-evaluation that
 * rewrites identical legs keeps the fingerprint.
 */
export const PROPOSAL_FINGERPRINT_LENGTH = 16;
/**
 * A single leg in a proposal response.
 */
export const ProposalLegResponseSchema = z.object({
    id: z.string().uuid(),
    kind: ProposalLegKindSchema,
    paymentId: z.string().uuid().nullable(),
    invoiceId: z.string().uuid().nullable(),
    supplierInvoiceId: z.string().uuid().nullable(),
    creditNoteId: z.string().uuid().nullable(),
    ledgerAccountId: z.string().uuid().nullable(),
    /**
     * Opaque client reference, populated only for a CLIENT_ALLOCATION leg. Null
     * for every other leg kind, and undefined on response shapes older than the
     * CLIENT_ALLOCATION leg kind.
     */
    clientId: z.string().uuid().nullable().optional(),
    /**
     * Opaque counterpart-bank-account reference, populated only for a TRANSFER
     * leg (the firm's own other bank account the money moved to or from). Null
     * for every other leg kind, and undefined on response shapes older than the
     * TRANSFER leg kind.
     */
    bankAccountId: z.string().uuid().nullable().optional(),
    /**
     * The invoice or supplier invoice a PAYMENT leg's recorded payment settles,
     * resolved server-side alongside `label` so the leg can link to the
     * document it names. Null for every other leg kind and for a payment that
     * settles no document; undefined on older response shapes.
     */
    paymentInvoiceId: z.string().uuid().nullable().optional(),
    paymentSupplierInvoiceId: z.string().uuid().nullable().optional(),
    /**
     * The invoice a CREDIT_NOTE leg's credit note was issued against. A credit
     * note has no page of its own and is shown on that invoice's page. Null for
     * every other leg kind and for a deleted credit note; undefined on older
     * response shapes.
     */
    creditNoteInvoiceId: z.string().uuid().nullable().optional(),
    amountMinorUnits: z.number().int().nullable(),
    matchReason: z.string().nullable(),
    /**
     * Human-readable description of what this leg settles: invoice number,
     * supplier name, credit note number, client, counterpart bank account, or
     * for a SPLIT leg `"<code> <name>"`, suffixed " (archived)" for an
     * archived account. A referenced record that is gone reads "(deleted)".
     * Null for a malformed leg missing its reference; undefined on older
     * response shapes.
     */
    label: z.string().nullable().optional(),
    /**
     * The structured record behind `label` (see `LegSubjectSchema`). Null only
     * for a CREATE_EXPENSE leg, which names nothing; undefined on older
     * response shapes.
     */
    subject: LegSubjectSchema.nullable().optional(),
});
// =============================================================================
// Proposal edit preview
// Panel-ready reconstruction of a multi-leg proposal, so a super-admin's
// in-place Edit opens the reconcile panel pre-filled from the proposal's legs.
// =============================================================================
/**
 * One money-in item in an edit preview. `match` is the resolved invoice (paid
 * items carry `classification: 'paid'` + `linkedPayment`); `mintAmountMinorUnits`
 * is the settlement amount for an unpaid (mint) leg, null for a paid (payment)
 * leg (the payment carries its own total).
 */
export const ProposalPreviewInvoiceItemSchema = z.object({
    match: ReconcilableInvoiceSchema,
    mintAmountMinorUnits: z.number().int().nullable(),
});
/** Money-out mirror of {@link ProposalPreviewInvoiceItemSchema}. */
export const ProposalPreviewExpenseItemSchema = z.object({
    match: ReconcilableSupplierInvoiceSchema,
    mintAmountMinorUnits: z.number().int().nullable(),
});
/** A signed GL split row (debit-positive, credit-negative). */
export const ProposalPreviewSplitSchema = z.object({
    ledgerAccountId: z.string().uuid(),
    amountMinorUnits: z.number().int(),
});
/**
 * One CLIENT_ALLOCATION item in an edit preview: a document-free client mint,
 * resolved to the client's current name server-side (the same batched
 * `findClientNamesByIds` lookup that resolves the CLIENT_ALLOCATION leg
 * label). `amountMinorUnits` is always positive (mirrors the leg-input
 * constraint - a zero/negative client mint is meaningless).
 */
export const ProposalPreviewClientItemSchema = z.object({
    clientId: z.string().uuid(),
    clientName: z.string(),
    amountMinorUnits: z.number().int().min(1),
});
/**
 * Panel-ready reconstruction of a multi-leg proposal. Exactly one of `invoices`
 * (money-in) / `expenses` (money-out) is populated per proposal; `splits` and
 * `clientAllocations` carry the signed GL residual rows and any document-free
 * client mints respectively, either of which may combine with the invoice/expense
 * side. Null / absent for single-leg proposals (which pre-fill via their chip,
 * except a lone CLIENT_ALLOCATION or SPLIT leg - see the leg-kind docs) and for
 * shapes the reconcile panels do not edit. `clientAllocations` is optional so a
 * response shape older than the CLIENT_ALLOCATION leg kind remains parseable;
 * a proposal carrying no CLIENT_ALLOCATION legs still returns an empty array.
 */
export const ProposalPreviewSchema = z.object({
    invoices: z.array(ProposalPreviewInvoiceItemSchema),
    expenses: z.array(ProposalPreviewExpenseItemSchema),
    splits: z.array(ProposalPreviewSplitSchema),
    clientAllocations: z.array(ProposalPreviewClientItemSchema).optional(),
});
// =============================================================================
// Bank Line Proposal - by-lines (read-only POST)
// POST /accounts/:accountId/bank-line-proposals/by-lines
// =============================================================================
/**
 * Request body for the by-lines re-hydration endpoint.
 * Accepts up to 100 line IDs (same limit as suggest-allocations, matching the
 * expected page size for the statement detail view).
 */
export const BankLineProposalsByLinesInputSchema = z.object({
    lineIds: z.array(z.string().uuid()).min(1).max(100),
});
/**
 * How far a SUGGESTED allocation is above its producer's suggest floor,
 * bucketed for presentation. Derived server-side by the producer that made
 * the score, never on the frontend: a raw `confidence` is on its own scorer's
 * scale and means nothing without knowing which producer and which
 * `scorerVersion` produced it (see `BankLineProposalResponseSchema.confidence`).
 *
 * `high` is shown as an ordinary suggestion. `low` earns a visible
 * "Check this one" marker, because the band it names is close to a coin flip
 * and a reviewer should read it before approving rather than after. Null on
 * an ABSTAINED decision (nothing was suggested) and on any producer whose
 * scorer is not calibrated for banding.
 */
export const AllocationConfidenceBandSchema = z.enum(['high', 'low']);
/**
 * A SUGGESTED allocation's confidence, ready to show: the band that decides
 * whether it carries a caution marker, and the percentage shown beside it.
 *
 * The two travel together because they are one measurement - `percent` is the
 * producer's own score, and `band` is which side of that producer's measured
 * cliff it falls on. Deriving one without the other invites a reader to invent
 * the missing half from a scale it does not know.
 *
 * `percent` is an integer 0-99, never 100: the model is never certain, and a
 * "100% confidence" label invites exactly the unread approval this tier exists
 * to discourage. See `randomForestConfidence` for the calibration evidence
 * that makes showing a percentage honest at all.
 */
export const AllocationConfidenceSchema = z.object({
    band: AllocationConfidenceBandSchema,
    percent: z.number().int().min(0).max(99),
});
/**
 * The LIVE classifier's active decision for one unallocated line, as the
 * by-lines read surfaces it. Makes "we looked and found nothing" an explicit
 * state: `outcome: 'ABSTAINED'` with a `summary` is Bill's reason it found
 * nothing, `outcome: 'SUGGESTED'` accompanies the proposal
 * built from the decision (its sentence is the proposal's `matchReason`, so
 * `summary` is null there), and a null `decision` on an unallocated line
 * means nothing has classified it - expected for a line that predates the
 * classifier (never backfilled by design), for which the UI renders nothing.
 * `summary` is a full plain-language sentence composed server-side, PII-free
 * (counts and document kinds only), never a token for a copy catalogue.
 */
export const BankLineDecisionSummarySchema = z.object({
    id: z.string().uuid(),
    outcome: ClassificationOutcomeSchema,
    decidedAt: z.string().datetime(),
    summary: z.string().nullable(),
    // Optional, not merely nullable: an already-loaded Bill bundle keeps calling
    // the API across a deploy, and `@packages/api` hard-fails a schema mismatch
    // with PARSE_ERROR rather than degrading. A required field would break the
    // by-lines read for every tab open against a backend revision that predates
    // it. Same reason the by-lines entry's own fields below are optional.
    confidence: AllocationConfidenceSchema.nullable().optional(),
});
/**
 * Extended per-line entry for the by-lines endpoint (Phase 6).
 *
 * Extends `LineSuggestionSchema` with optional proposal metadata:
 *   - `noteForCustomer` - note set by a super-admin for the customer's context.
 *   - `legs`            - raw proposal legs for the super-admin edit round-trip.
 *   - `proposalId`      - present when a persisted proposal exists for the line,
 *                         even when `suggestion` is null (multi-leg / PAYMENT /
 *                         SPLIT proposals that don't map to a single chip).
 *
 * Fields are optional so older responses (before Phase 6) remain parseable.
 */
export const BankLineProposalsByLinesLineSuggestionSchema = LineSuggestionSchema.extend({
    // Overrides `LineSuggestionSchema`'s live-path chip with the rehydrated one:
    // this entry echoes a persisted leg's `matchReason`, whose vocabulary is the
    // writing producer's (a token from the cascade, a whole sentence from
    // `bank-allocation-rf`), not the closed set the live route derives. See
    // `RehydratedAllocationLegSchema`.
    suggestion: RehydratedAllocationLegSchema.nullable(),
    noteForCustomer: z.string().nullable().optional(),
    legs: z.array(ProposalLegResponseSchema).optional(),
    // null when no persisted proposal exists; undefined on older response shapes.
    proposalId: z.string().uuid().nullable().optional(),
    // The active proposal's updatedAt; orders several reads of one line.
    // Absent when no active proposal exists for the line.
    proposalUpdatedAt: z.string().datetime().optional(),
    // Hash of what the active proposal would post (see
    // PROPOSAL_FINGERPRINT_LENGTH). Absent when no active proposal exists.
    proposalFingerprint: z.string().length(PROPOSAL_FINGERPRINT_LENGTH).optional(),
    // Proposal source – null when no persisted proposal exists for the line,
    // undefined on older response shapes that pre-date the field. Defaults to
    // 'SYSTEM' on the frontend when absent.
    source: ProposalSourceSchema.nullable().optional(),
    // Panel-ready reconstruction for in-place Edit of a multi-leg proposal.
    // Null for single-leg proposals (they pre-fill via the chip) and no-proposal
    // lines; undefined on older response shapes.
    preview: ProposalPreviewSchema.nullable().optional(),
    // The LIVE producer's rank-1 score at classification time (see
    // BankLineProposalResponseSchema.confidence). Null on MANUAL/curated
    // proposals, pre-column rows, and no-proposal lines; undefined on older
    // response shapes.
    confidence: z.number().nullable().optional(),
    // Dismiss curator annotation. On by-lines, present only when includeDismissed
    // is on (super-admin). On GET /bank-line-proposals/:lineId, present for
    // ai-assistant and super-admin when the line has a dismissed proposal.
    // Absent (undefined) for every other response entry: no persisted proposal,
    // an active (non-dismissed) proposal, or a caller who cannot see dismiss
    // state. Keeping these optional (rather than always-present-nullable, as on
    // BankLineProposalResponseSchema) means a member's by-lines response is
    // byte-for-byte unchanged – undefined optionals are omitted from the JSON.
    dismissedAt: z.string().datetime().nullable().optional(),
    dismissNote: z.string().nullable().optional(),
    dismissedByUserId: z.string().uuid().nullable().optional(),
    // True when the MANUAL proposer's account membership role is `ai-assistant`.
    // Present (true|false) when a proposal exists; omit/undefined on no-proposal
    // rows. Never true for SYSTEM / null-proposer / human roles.
    proposedByAiAssistant: z.boolean().optional(),
    // Present when a human member of the account (not the ai-assistant) drafted
    // the proposal; absent for SYSTEM, ai-assistant and seatless super-admin drafts.
    proposedByMember: z.object({ name: z.string().nullable() }).optional(),
    // The LIVE classifier's active decision for the line (see
    // BankLineDecisionSummarySchema). Null when no active decision exists;
    // undefined on response shapes that pre-date the field.
    decision: BankLineDecisionSummarySchema.nullable().optional(),
    // The classification decision this entry's OWN persisted proposal was
    // built from (BankStatementLineProposal.decisionId, mirroring
    // BankLineProposalResponseSchema.decisionId) - not to be confused with
    // `decision`, the LIVE decision above. `seedSystemProposalsForLines`
    // writes the classifier's decision for every line in a batch, then
    // persists proposals afterwards in bounded chunks, so a poll can observe
    // a line's LIVE decision having already moved to a new id while this
    // entry's proposal still carries the previous one - a torn read, not a
    // converged one. A reader settling on this response (e.g.
    // settleBankLineProposalCaches) must keep polling until `decisionId`
    // matches `decision?.id`, rather than treating the two consecutive
    // identical reads of that torn state as convergence. Null when no
    // persisted proposal exists for the line, or on a MANUAL/curated
    // proposal; undefined on response shapes that pre-date the field.
    decisionId: z.string().nullable().optional(),
});
/**
 * Response for the by-lines endpoint: one entry per requested line ID.
 *
 * Extends `BatchSuggestAllocationsResponse` with proposal metadata so the
 * frontend can show customer notes and super-admin curation controls without
 * a separate per-line fetch.
 *
 * `suggestion` is null when no persisted proposal exists for the line, or when
 * the proposal has a leg kind that does not map to a single-document chip
 * (PAYMENT / SPLIT / multi-leg).
 */
export const BankLineProposalsByLinesResponseSchema = z.object({
    suggestions: z.array(BankLineProposalsByLinesLineSuggestionSchema),
});
// Re-export for convenience (avoids a separate import from bank-statement.ts in route files).
export { LineSuggestionSchema };
// =============================================================================
// Update Proposal (PUT /accounts/:accountId/bank-line-proposals/:lineId)
// =============================================================================
/**
 * A single typed leg for a proposal curation request.
 * The required foreign key(s) differ by leg kind:
 *   PAYMENT    - paymentId (amountMinorUnits is null; payment carries its own amount)
 *   INVOICE    - invoiceId + amountMinorUnits (positive, outstanding amount)
 *   EXPENSE    - supplierInvoiceId + amountMinorUnits (positive, settlement amount)
 *   CREDIT_NOTE - creditNoteId + amountMinorUnits (positive, settlement amount)
 *   SPLIT      - ledgerAccountId + amountMinorUnits (signed, debit-positive)
 *   CLIENT_ALLOCATION - clientId + amountMinorUnits (positive) - a document-free
 *     client mint. clientId ownership is verified at the route (an active
 *     client of the account); shape (presence + positive amount) is validated
 *     service-side. Meaning (business credit / trust receipt / trust payment /
 *     client debit draining an overpayment credit) is derived from the line's
 *     sign and bank kind at approve time - all four quadrants post.
 *   CREATE_EXPENSE - NOTHING. No foreign key, no amount: it is a flag, not a
 *     posting instruction, so supplying an amount is rejected service-side
 *     rather than ignored. It must also be the proposal's ONLY leg - it cannot
 *     be combined with a posting leg, because the resulting proposal would be
 *     partly postable and partly advisory.
 *   TRANSFER - bankAccountId (the counterpart bank account) + amountMinorUnits
 *     (the SIGNED line amount). Must be the proposal's ONLY leg (a transfer
 *     covers the whole line by construction) and may carry no other foreign
 *     key. bankAccountId ownership is verified at the route; shape is
 *     validated service-side.
 */
export const UpdateProposalLegInputSchema = z.object({
    kind: ProposalLegKindSchema,
    paymentId: z.string().uuid().optional(),
    invoiceId: z.string().uuid().optional(),
    supplierInvoiceId: z.string().uuid().optional(),
    creditNoteId: z.string().uuid().optional(),
    ledgerAccountId: z.string().uuid().optional(),
    clientId: z.string().uuid().optional(),
    bankAccountId: z.string().uuid().optional(),
    amountMinorUnits: z.number().int().optional(),
});
/**
 * Request body for PUT /accounts/:accountId/bank-line-proposals/:lineId.
 * Replaces all existing legs and optionally sets a customer-visible note.
 *
 * Leg kind/amount validation:
 *   - At least `PROPOSAL_LEG_MIN`, at most `PROPOSAL_LEG_MAX` legs.
 *   - A CREDIT_NOTE leg cannot coexist with an EXPENSE or PAYMENT leg in the same
 *     proposal.
 *   - PAYMENT legs require paymentId; all others require amountMinorUnits and their
 *     respective foreign key (invoiceId, supplierInvoiceId, creditNoteId, ledgerAccountId,
 *     clientId).
 *   - CLIENT_ALLOCATION legs additionally require a positive amountMinorUnits (a
 *     zero/negative client mint is meaningless).
 *
 * Cross-sign validation (money-in vs money-out) is deferred to approve time,
 * when the engine has access to the line's sign.
 */
export const PROPOSAL_LEG_MIN = 1;
/**
 * The upper bound on persisted legs for one proposal. Raised from 10 to 30 to
 * carry a real batch receipt: a single deposit settling a dozen-plus invoices,
 * plus any fee split alongside them. Approve is linear in leg count and the
 * proposal card maps over `legs` with no layout cap, so the bound exists to
 * keep the payload finite rather than to protect either consumer.
 */
export const PROPOSAL_LEG_MAX = 30;
export const UpdateProposalBodySchema = z.object({
    legs: z.array(UpdateProposalLegInputSchema).min(PROPOSAL_LEG_MIN).max(PROPOSAL_LEG_MAX),
    noteForCustomer: z.string().max(500).optional(),
});
/**
 * Response shape for a persisted proposal (with legs).
 * Returned by PUT (update), POST dismiss, and similar curation mutations.
 */
export const BankLineProposalResponseSchema = z.object({
    id: z.string().uuid(),
    bankStatementLineId: z.string().uuid(),
    bankAccountId: z.string().uuid(),
    source: ProposalSourceSchema,
    dismissedAt: z.string().datetime().nullable(),
    noteForCustomer: z.string().nullable(),
    proposedByUserId: z.string().uuid().nullable(),
    // Optional reason given when dismissing, and the dismissing user. Always
    // present in the response (never undefined); both null on a proposal that
    // has never been dismissed, or on one re-curated after a dismiss (the
    // upsertProposalLegs un-dismiss path clears both). Also both null on the
    // POST dismiss endpoint's response to a REPLAY call (the caller observed
    // an already-dismissed row rather than performing the write) - the route
    // nulls them so a replaying caller cannot read the original dismisser's
    // note/identity; the persisted row itself is unaffected. See
    // `dismissProposal` in bank-line-proposal.service.ts.
    dismissNote: z.string().nullable(),
    dismissedByUserId: z.string().uuid().nullable(),
    computedAt: z.string().datetime().nullable(),
    // The classification decision (domains/classification, BANK_LINE_ALLOCATION
    // problem) this SYSTEM proposal was built from - an opaque cross-domain id,
    // not a resource this API exposes on its own. Null on MANUAL proposals
    // (curated by a human, not a classifier decision) and on rows seeded before
    // this column existed.
    decisionId: z.string().nullable(),
    // The LIVE producer's rank-1 score at classification time, on that scorer's
    // own scale - comparable only within one scorerVersion, never across
    // producers. Null on MANUAL/curated proposals and on rows seeded before the
    // column existed.
    //
    // Not a probability in general - a scorer is free to emit any scale, and
    // `proposal-cascade`'s are rule-tier constants. `bank-allocation-rf`'s
    // specifically HAS been measured as calibrated (see `randomForestConfidence`),
    // which is why the decision summary can carry a percentage; that is a fact
    // about one scorer, established by measurement, not a property of this field.
    confidence: z.number().nullable(),
    updatedAt: z.string().datetime(),
    createdAt: z.string().datetime(),
    // Named legs, with SPLIT legs on the same ledger account merged as approve
    // posts them.
    legs: z.array(ProposalLegResponseSchema),
    // True when proposedByUserId resolves to an `ai-assistant` membership on the
    // account. False when the proposer is human, missing, or has no membership
    // (e.g. super-admin without a seat). Derived at read time; not stored.
    proposedByAiAssistant: z.boolean(),
    // See PROPOSAL_FINGERPRINT_LENGTH. Undefined on older response shapes.
    proposalFingerprint: z.string().length(PROPOSAL_FINGERPRINT_LENGTH).optional(),
});
// =============================================================================
// Dismiss Proposal (POST /accounts/:accountId/bank-line-proposals/:lineId/dismiss)
// =============================================================================
/**
 * Request body for the dismiss endpoint.
 * `note` is optional free text (max 500 characters, same bound as
 * `noteForCustomer`) explaining why the proposal was rejected. Blank/whitespace
 * normalises to null (see `nullableText`). Omit the field entirely for a
 * plain one-click dismiss with no reason recorded.
 */
export const DismissProposalBodySchema = z.object({
    note: nullableText(500, 'Reason must be 500 characters or less'),
});
// =============================================================================
// Approve Proposal (POST /accounts/:accountId/bank-line-proposals/:lineId/approve)
// =============================================================================
/**
 * Request body for the approve endpoint.
 * An idempotencyKey is REQUIRED: this endpoint posts to the ledger, so the
 * caller must supply a deterministic key so re-submissions are safe.
 */
export const ApproveBankLineProposalBodySchema = z.object({
    idempotencyKey: z.string().min(1).max(255),
});
/**
 * Response for POST /approve.
 *
 * For a reconcile-dispatched proposal (every leg kind except TRANSFER) this is
 * the `ReconcileMoneyInResponse` / `ReconcileMoneyOutResponse` shape: the bank
 * statement line with its `ledgerEntrySetId` now set, plus the posted ledger
 * entry set (`entrySet` non-null, `bankTransferId` null).
 *
 * For a TRANSFER proposal the approve dispatches to the bank-transfer engine,
 * which posts this side's leg and returns the transfer entity rather than an
 * entry set: `entrySet` is null and `bankTransferId` names the (PENDING or
 * COMPLETE) bank transfer. The line is still returned allocated - its own leg
 * posted through Transfer Clearing.
 */
export const ApproveBankLineProposalResponseSchema = z.object({
    line: BankStatementLineSchema,
    entrySet: LedgerEntrySetSchema.nullable(),
    // Undefined on response shapes older than the TRANSFER leg kind.
    bankTransferId: z.string().uuid().nullable().optional(),
});
// =============================================================================
// Approve several proposals (POST /accounts/:accountId/bank-line-proposals/approve)
// =============================================================================
/** Most lines one batch approve takes, so a button press finishes well inside its deadline. */
export const MAX_BATCH_APPROVE_LINES = 25;
/**
 * Request body for the batch approve. Each line carries the
 * `proposalFingerprint` of the suggestion the user was shown: a line whose
 * suggestion has changed since is refused (`stale-version`) and the others
 * still post. `idempotencyKey` covers the whole request; each line posts under
 * its own key derived from it, so a retry re-attempts only what did not post.
 */
export const ApproveBankLineProposalsBodySchema = z.strictObject({
    idempotencyKey: z.string().min(1).max(255),
    lines: z
        .array(z.strictObject({
        lineId: LowercaseUuidSchema,
        expectedFingerprint: z.string().length(PROPOSAL_FINGERPRINT_LENGTH),
    }))
        .min(1)
        .max(MAX_BATCH_APPROVE_LINES)
        .refine((lines) => new Set(lines.map((line) => line.lineId)).size === lines.length, {
        message: 'Each line may appear only once',
    }),
});
/**
 * Why a line was not approved:
 *   - `stale-version` - the suggestion changed since the user was shown it;
 *   - `conflict`      - the line is already allocated, or the suggestion was dismissed;
 *   - `not-found`     - the line has no suggestion, or a record it names is gone;
 *   - `invalid`       - the suggestion cannot post as it stands (a CREATE_EXPENSE
 *                       flag, a leg that does not suit the line's direction, a stale match);
 *   - `unexpected`    - an internal error, and a re-read found the line still unallocated.
 */
export const BatchApproveRefusalReasonSchema = z.enum(['stale-version', 'conflict', 'not-found', 'invalid', 'unexpected']);
/**
 * One line's outcome. `approved` posted exactly what the pinned fingerprint
 * covered, so each invoice now owes the `outstandingAfterMinorUnits` that read
 * showed. `refused` posted nothing for this line. `unknown` means an internal error
 * left it unclear whether the line posted: check the line before retrying.
 */
export const ApproveBankLineProposalsLineResultSchema = z.object({
    lineId: z.string().uuid(),
    /** The line's transaction date, `YYYY-MM-DD`. */
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** Signed: negative is money out. */
    amountMinorUnits: z.number().int(),
    currency: z.string(),
    outcome: z.enum(['approved', 'refused', 'unknown']),
    refusal: z.object({ reason: BatchApproveRefusalReasonSchema, message: z.string() }).optional(),
});
/** One result per requested line, in request order, with the counts. */
export const ApproveBankLineProposalsResponseSchema = z.object({
    results: z.array(ApproveBankLineProposalsLineResultSchema),
    approvedCount: z.number().int().min(0),
    refusedCount: z.number().int().min(0),
    unknownCount: z.number().int().min(0),
});
// =============================================================================
// Bank Statement Proposal Run
// GET /accounts/:accountId/bank-statements/:statementId/proposal-run
// =============================================================================
/**
 * Status of the async scorer job for a bank statement.
 *
 * - COMPUTING: Scorer is in progress; `scoredLines` advances as lines are scored.
 * - READY:     All lines scored; persisted proposals are available for all lines.
 *
 * Transient compute failures propagate to Pub/Sub for retry rather than being
 * recorded as a terminal FAILED state. Poll until READY.
 */
export const ProposalRunStatusSchema = z.enum(['COMPUTING', 'READY']);
/**
 * Response for the proposal-run endpoint.
 * Returns null-equivalent (404) when no run exists for the statement
 * (e.g. legacy/manual lines with `bankStatementId IS NULL`).
 */
export const ProposalRunResponseSchema = z.object({
    status: ProposalRunStatusSchema,
    totalLines: z.number().int().min(0),
    scoredLines: z.number().int().min(0),
});
// =============================================================================
// GET /accounts/:accountId/bank-line-proposals/:lineId
// Single-line proposal retrieval including dismissed state
// =============================================================================
/**
 * Response for GET /accounts/:accountId/bank-line-proposals/:lineId.
 * Returns the rehydrated proposal for a single line, including dismiss state
 * when the caller is ai-assistant or super-admin.
 *
 * `proposal` is null when no proposal exists for the line (neither active nor dismissed).
 * When a proposal exists (active or dismissed), returns the rehydrated suggestion entry.
 */
export const BankLineProposalGetResponseSchema = z.object({
    proposal: BankLineProposalsByLinesLineSuggestionSchema.nullable(),
});
// =============================================================================
// GET /accounts/:accountId/bank-line-proposals
// Every suggestion waiting for approval, across the firm's bank accounts
// =============================================================================
/** Most waiting suggestions one page of the composed read returns. */
export const MAX_WAITING_PROPOSALS_PAGE = 25;
/**
 * Query for the waiting-suggestions read. `cursor` is the `nextCursor` of the
 * previous page (a bank line id): paging is keyset, so approving a page's lines
 * never makes the next page skip any.
 */
export const ListBankLineProposalsQuerySchema = z.strictObject({
    bankAccountId: LowercaseUuidSchema.optional(),
    from: CalendarDateOnlySchema.optional(),
    to: CalendarDateOnlySchema.optional(),
    cursor: LowercaseUuidSchema.optional(),
    limit: z.coerce.number().int().min(1).max(MAX_WAITING_PROPOSALS_PAGE).prefault(MAX_WAITING_PROPOSALS_PAGE),
});
/**
 * One unallocated bank line with a suggestion waiting for approval, ready to
 * present: the line, its bank account's name, who or what suggested it, and the
 * named legs (SPLIT legs on one account merged, as approve posts them).
 *
 * `amountMinorUnits` is signed: negative is money out. `confidence` is the
 * scorer's banded measure and is null on a drafted (MANUAL) suggestion, which
 * carries `draftedBy` instead. `approvable` is false when approve would refuse
 * or over-pay as the suggestion stands (a CREATE_EXPENSE flag, or a lone
 * invoice or expense leg whose document is already paid or gone).
 */
export const WaitingAllocationSchema = z.object({
    lineId: z.string().uuid(),
    proposalFingerprint: z.string().length(PROPOSAL_FINGERPRINT_LENGTH),
    /** When the suggestion last changed: orders this row against a later write about the same line when buttons are built. */
    proposalUpdatedAt: z.string().datetime(),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    description: z.string(),
    amountMinorUnits: z.number().int(),
    currency: z.string(),
    bankAccountId: z.string().uuid(),
    bankAccountName: z.string(),
    source: ProposalSourceSchema,
    confidence: AllocationConfidenceSchema.nullable(),
    draftedBy: z.object({ name: z.string().nullable(), isAssistant: z.boolean() }).nullable(),
    approvable: z.boolean(),
    noteForCustomer: z.string().nullable(),
    legs: z.array(ProposalLegResponseSchema),
});
export const ListBankLineProposalsResponseSchema = z.object({
    data: z.array(WaitingAllocationSchema),
    /** Every waiting suggestion the filters match, not only this page. */
    total: z.number().int().min(0),
    nextCursor: z.string().uuid().nullable(),
    hasMore: z.boolean(),
});
